import { BarElement, CategoryScale, Chart, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import { Bar } from "react-chartjs-2";

// Register chart.js components
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const groupByDateAndPodcast = (data: SegmentSearchAggregateData) => {
  const groupedData: Record<string, Record<string, number>> = {};

  data
    .filter((item) => !!(item.published_at || item.episode_created_at))
    .forEach((item) => {
      let dateToUse = new Date(item.published_at || item.episode_created_at);
      if (dateToUse.getTime() === 0) {
        dateToUse = new Date(item.episode_created_at);
      }
      // Extract date month and year only
      const date = dateToUse.toISOString().split("T")[0].slice(0, 7);
      const podcast = item.podcast_title; // Extract podcast

      if (!groupedData[date]) {
        groupedData[date] = {};
      }

      if (!groupedData[date][podcast]) {
        groupedData[date][podcast] = 1;
      } else {
        groupedData[date][podcast]++;
      }
    });

  return groupedData;
};

export const KeywordChart = ({
  aggregateData,
  ...props
}: React.HTMLProps<HTMLDivElement> & {
  aggregateData: SegmentSearchAggregateData;
}) => {
  const groupedDataPodcast = groupByDateAndPodcast(aggregateData);

  return (
    <div {...props}>
      <StackedBarChart data={groupedDataPodcast} />
    </div>
  );
};

const prepareChartData = ({
  groupedData,
  locale,
}: {
  groupedData: Record<string, Record<string, number>>;
  locale: string;
}) => {
  const labels = Object.keys(groupedData); // Dates
  labels.sort(); // Sort dates
  const podcasts = Array.from(new Set(Object.values(groupedData).flatMap((d) => Object.keys(d)))); // Get all unique podcast names

  const humanReadableLabels = labels.map((date) => {
    const dateObj = new Date(date); // Convert string to Date object
    return dateObj.toLocaleDateString(locale, {
      year: "numeric",
      month: "2-digit",
    }); // Format the date based on user's locale
  });

  const datasets = podcasts.map((podcast) => {
    return {
      label: podcast,
      data: labels.map((date) => groupedData[date][podcast] || 0), // Mention count for each podcast on each date
      backgroundColor: getRandomColor({ podcastId: podcast }), // Optional: Generate random color for each podcast
    };
  });

  return { labels: humanReadableLabels, datasets };
};

const getRandomColor = ({ podcastId }: { podcastId: string }) => {
  const hash = Array.from(podcastId).reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc); // Hash function using bitwise shifting
  }, 0);

  // Convert hash to a valid hex color
  let color = "#";
  for (let i = 0; i < 3; i++) {
    // We need 3 parts (RGB)
    const value = (hash >> (i * 8)) & 0xff; // Extract each byte of the hash
    color += ("00" + value.toString(16)).slice(-2); // Convert to hex and ensure 2 digits
  }

  return color;
};

const StackedBarChart = ({ data }: { data: Record<string, Record<string, number>> }) => {
  const t = useTranslations();
  const locale = useLocale();
  const chartData = prepareChartData({
    groupedData: data,
    locale,
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: t("podcastInsights.chartTitle"),
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  return <Bar data={chartData} options={options} />;
};
